import React from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import GettingStarted from '@/components/marketing/GettingStarted';
import CommonQuestions from '@/components/marketing/CommonQuestions';
import AttributionGap from '@/components/marketing/AttributionGap';
import ReachBenchmark from '@/components/marketing/ReachBenchmark';
import ProofStrip from '@/components/marketing/ProofStrip';
import { CBO_COUNT, LANGUAGES, contactHref } from '@/components/marketing/siteFacts';
import {
  ArrowRight,
  Heart,
  CheckCircle,
  Users,
  BarChart3,
  Target,
  Globe,
  Globe2,
  Award,
  MessageSquare,
  Radio,
  Landmark,
} from 'lucide-react';

/**
 * For health plans & counties — the funders.
 *
 * Leads with the line the plan buyer actually needs: a contract says an
 * organization should be delivering; the ledger says whether it did. The
 * evidence is the seed deck's (cohort engagement against the CHCS benchmark),
 * read from siteFacts. The 35–45% engagement, 40% churn and unattributed ROI
 * figures this page used to carry are gone.
 */
export default function HealthPlansSolutionsPage() {
  usePageMeta(SEO_ROUTES['/solutions/health-plans']);

  const approach = [
    {
      icon: Users,
      title: `${CBO_COUNT} community organizations`,
      body: 'Faith-based organizations, nonprofits and CBOs that have earned trust in their neighborhoods over decades — the door members already walk through.',
    },
    {
      icon: MessageSquare,
      title: `${LANGUAGES} languages`,
      body: 'Text, WhatsApp, voice or in person, in the member’s language, with consent recorded on every exchange.',
    },
    {
      icon: BarChart3,
      title: 'Delivery as a record, not an estimate',
      body: 'Which member, what service, which organization, on what date — reported back through your own channel, with proof it was not billed elsewhere.',
    },
  ];

  const programs = [
    { icon: Award, title: 'CalAIM ECM activation', body: 'Connect eligible members to Enhanced Care Management through trusted community touchpoints.' },
    { icon: Target, title: 'HEDIS gap closure', body: 'Reach members for preventive screenings, immunizations and chronic care follow-ups.' },
    { icon: Users, title: 'Medicaid enrollment & renewal', body: 'Support redetermination and new enrollment through community education and assistance.' },
    { icon: Heart, title: 'Chronic care programs', body: 'Engage members in diabetes prevention, hypertension management and behavioral health.' },
    { icon: Globe, title: 'Community Supports & SDOH navigation', body: 'Connect members to housing, food, transportation and financial assistance, attributed to the organization that delivered it.' },
    { icon: MessageSquare, title: 'Member communication', body: 'Deliver plan updates, benefits education and health campaigns through trusted channels.' },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="bg-paper border-b border-v2border py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 bg-v2accent-soft rounded-full mb-6">
              <Landmark className="w-5 h-5 text-v2accent mr-2" />
              <span className="text-ink font-medium">For Health Plans &amp; Counties</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-ink mb-6">
              Know who delivered the care.<br />
              <span className="text-v2accent">Not just who billed it.</span>
            </h1>
            <p className="text-xl text-ink-500 mb-4">
              A contract tells you an organization should be delivering. WeaveIn tells you whether it did — which member,
              what service, which organization, on what date — as a record rather than an estimate filed and never
              reconciled.
            </p>
            <p className="text-base text-ink-400 mb-8">
              And it reaches the members your letters and calls do not, through the community organizations in their own
              neighborhood, in their language, on the number they actually pick up.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to={contactHref('fund')}
                className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors"
              >
                Talk to us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/impact"
                className="inline-flex items-center px-8 py-4 bg-v2accent-soft text-ink text-lg font-semibold rounded-lg hover:bg-ink/20 transition-colors"
              >
                See the evidence
              </Link>
            </div>
          </div>
        </div>
      </section>

      <AttributionGap />

      {/* The trust network approach */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Reach through the communities members trust
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Instead of pushing messages from the plan, the outreach comes from community organizations your members
              already know.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {approach.map((a) => (
              <div key={a.title} className="flex items-start">
                <div className="w-12 h-12 bg-v2accent rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <a.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-ink dark:text-white mb-2">{a.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300">{a.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ReachBenchmark heading="Engagement, reproducible across federations" />

      <ProofStrip labels={['Members in cohort', 'Validated ECM enrollments', 'Care navigations', 'Outreach touchpoints']} />

      {/* Why now: delegation becomes a mixed model */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-ink dark:text-white mb-4 text-center">
            The payer keeps moving. Every move needs a ledger.
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto text-center mb-10">
            About 2M Medi-Cal members are moving between managed care and fee-for-service, and HR1 pushes the same shift
            through every state. Plans are now paid to retain members and must prove the care happened; providers become
            the direct biller.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Delegate a population to the network</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                One delegated pathway, priced at $8–25 PMPM or per verified outcome. The same encounter can also be
                billed to the plan’s Community Supports menu and to grants that stack on top — with non-duplication
                proof on every line.
              </p>
              <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                {['Credentialed community delivery, closed-loop to the record', 'Per-funder attribution and CalAIM-ready artifacts', 'Program performance visible as it happens'].map((t) => (
                  <li key={t} className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Submitted your way, in any state</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Your service menu, rates, modifiers and filing windows are held as data. Encounters reach you through your
                own channel — 837, portal, workbook — not a new one, and any U.S. payer is reachable through national
                clearinghouse connections.
              </p>
              <Link to="/funder-coverage" className="inline-flex items-center font-semibold text-ink hover:underline">
                <Globe2 className="w-4 h-4 mr-2" aria-hidden="true" />
                See funder coverage
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-ink dark:text-white mb-12 text-center">
            Programs we support
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programs.map((p) => (
              <div key={p.title} className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <div className="w-10 h-10 bg-v2accent-soft rounded-lg flex items-center justify-center mb-4">
                  <p.icon className="w-5 h-5 text-v2accent" />
                </div>
                <h3 className="text-lg font-bold text-ink dark:text-white mb-2">{p.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">{p.body}</p>
              </div>
            ))}
          </div>
          <Link
            to="/solutions/emergency-response"
            className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:border-v2accent transition-colors"
          >
            <Radio className="w-8 h-8 text-v2accent shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <p className="font-bold text-gray-900 dark:text-white">Use case: emergency response</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Reach members by geography during a wildfire, outbreak or evacuation, and verify who is safe and who is
                unreachable.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-ink" aria-hidden="true" />
          </Link>
        </div>
      </section>

      {/* Partnership model */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-v2accent rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-white">
                <h3 className="text-xl font-bold mb-4">Start with one program</h3>
                <ul className="space-y-3">
                  {['A defined population, measurable before scale', 'Performance-based pricing available', 'Pilot first, then expand'].map((t) => (
                    <li key={t} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-v2accent mr-3 mt-0.5 flex-shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-white">
                <h3 className="text-xl font-bold mb-4">Through your own channel</h3>
                <ul className="space-y-3">
                  {['Member files read in your own format', 'Encounters returned the way you take them', 'HIPAA-compliant, with a BAA'].map((t) => (
                    <li key={t} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-v2accent mr-3 mt-0.5 flex-shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-paper border-b border-v2border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-ink mb-6">
            See delivery you can pay against
          </h2>
          <p className="text-xl text-ink-500 mb-10">
            Map where your plan sits — delegated, fee-for-service or both — and walk through a live encounter from one of your
            programs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <Link
              to={contactHref('fund')}
              className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors shadow-lg"
            >
              Talk to us
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/impact"
              className="inline-flex items-center px-8 py-4 bg-transparent text-ink text-lg font-semibold rounded-lg border-2 border-ink hover:bg-ink hover:text-paper transition-colors"
            >
              See the evidence
            </Link>
          </div>
        </div>
      </section>

      <CommonQuestions
        questions={[
          {
            q: 'We already contract with community organizations. What does this add?',
            a: 'A contract tells you an organization should be delivering. This tells you whether it did — which member, what service, which organization, on what date — as a record rather than an estimate filed and never reconciled.',
          },
          {
            q: 'Can members be reached in their own language?',
            a: 'Yes, by text, WhatsApp, voice or in person, through the community organizations members already trust, with consent recorded on every exchange.',
          },
          {
            q: 'We are not in California. Does this work for our plan?',
            a: 'Yes. Plan rules are held as data with effective dates, and any U.S. payer is reachable through national clearinghouse connections. California’s Medi-Cal plans have the deepest rules today; adding yours is a configuration change, not a release.',
          },
        ]}
      />

      <GettingStarted />

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
}
